import { ID } from '@datorama/akita';
import { environment as env } from 'env/environment';
import { Observable, of } from 'rxjs';

import { Entity, OneToMany, Column, OneToOne } from '../entity';
import { ColumnType, LanguagePage, PageTitles } from '../interfaces/types';
import { IEntity } from '../interfaces/models';
import { DropPermission } from './dropPermission.model';
import { DropPermissionService } from '../services/dropPermission';
import { Knock } from './knock.model';
import { KnockService } from '../services/knock';
import { Address } from './address.model';
import { AddressService } from '../services/address';
import { AddressHistory } from './addressHistory.model';
import { AddressHistoryRepository } from '../services/addressHistory';
import { Ams } from './ams.model';
import { AmsService } from '../services/ams';
import { Result } from '../interfaces/helpers';

export interface IAddress360 extends IEntity {
  // table fields
  tv: boolean;
  hsia: boolean;
  hp: boolean;
  mob: boolean;
  shs: boolean;
  // table and detail fields
  serviceAddress: string;
  city: string;
  province: string;
  premiseType: string;
  partnerStatus: string;
  sayb: string;
  availableTech: string;
  currentTech: string;
  // detail fields
  addressComment: string;
  coid: string;
  fsa: string;
  fmsId: string;
  gisDropStatus: string;
  fibreDropCompletionDate: Date;
  firstNation: string;
  assetSummary: string;
  servicesAvailable: string;
  existingBusinessProducts: string;
  dropStatus: boolean;
  addressActivity?: Observable<Address>;
  dropPermission?: Observable<Result<DropPermission>>;
  knocks?: Observable<Result<Knock>>;
  addressHistory?: Observable<Result<AddressHistory>>;
  comments?: Observable<Ams>;
}

@Entity({
  name: PageTitles.address360,
  language: LanguagePage.address360,
})
export class Address360 implements IAddress360 {
  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 1,
    },
  })
  serviceAddress: string;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 2,
    },
  })
  premiseType: string;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 3,
      hide: true,
    },
  })
  currentTech: string;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 4,
      hide: true,
    },
  })
  availableTech: string;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 5,
      hide: true,
    },
  })
  sayb: string;

  @Column({
    type: ColumnType.boolean,
    table: {
      defaultPosition: 6,
      hide: true,
    },
  })
  tv: boolean;

  @Column({
    type: ColumnType.boolean,
    table: {
      defaultPosition: 7,
      hide: true,
    },
  })
  hsia: boolean;

  @Column({
    type: ColumnType.boolean,
    table: {
      defaultPosition: 8,
      hide: true,
    },
  })
  hp: boolean;

  @Column({
    type: ColumnType.boolean,
    table: {
      defaultPosition: 9,
      hide: true,
    },
  })
  mob: boolean;

  @Column({
    type: ColumnType.boolean,
    table: {
      defaultPosition: 10,
      hide: true,
    },
  })
  shs: boolean;

  @OneToOne({
    repository: AddressService,
    api: (i: Address360) => `/${i.id}`,
  })
  addressActivity: Observable<Address>;

  @OneToOne({
    repository: AmsService,
    api: (i: Address360) => `address/${i.id}/comment`,
    headers: env.headers.ams,
  })
  comments: Observable<Ams>;

  @OneToMany({
    repository: KnockService,
    expression: { addressIds: (i: Address360) => [i.id] },
  })
  knocks: Observable<Result<Knock>>;

  @OneToMany({
    repository: AddressHistoryRepository,
    api: (i: Address360) => `/${i.id}/history`,
  })
  addressHistory: Observable<Result<AddressHistory>>;

  @OneToMany({
    repository: DropPermissionService,
    expression: { addressId: (i: Address360) => i.id },
  })
  dropPermission: Observable<Result<DropPermission>>;

  id: ID;
  postalCode?: string;
  city: string;
  province: string;
  addressComment: string;
  coid: string;
  fsa: string;
  firstNation: string;
  //knockStatus: string;
  servicesAvailable: string;
  //homeSecurityEligible: boolean;
  //assetRecontracting: string;
  //dropPermissionStatus: string;
  gisDropStatus: string;
  dropStatus: boolean;
  fmsId: string;
  fibreDropCompletionDate: Date;
  href?: string;
  createdBy: string;
  createdAt: Date;
  modifiedBy: string;
  modifiedAt: Date;
  assetSummary: string;
  existingBusinessProducts: string;
  partnerStatus: string;

  constructor(
    address360: IAddress360 = {
      id: '',
      serviceAddress: '',
      city: '',
      province: '',
      premiseType: '',
      sayb: '',
      availableTech: '',
      currentTech: '',
      tv: false,
      hsia: false,
      hp: false,
      mob: false,
      shs: false,
      dropStatus: false,
      fmsId: '',
      gisDropStatus: '',
      fibreDropCompletionDate: new Date(),
      addressComment: '',
      coid: '',
      fsa: '',
      firstNation: '',
      servicesAvailable: '',
      assetSummary: '',
      existingBusinessProducts: '',
      partnerStatus: '',
      createdBy: '',
      createdAt: new Date(),
      modifiedBy: '',
      modifiedAt: new Date(),
      addressActivity: of({} as Address),
      knocks: of({ items: [] } as Result<Knock>),
      dropPermission: of({ items: [] } as Result<DropPermission>),
    }
  ) {
    const {
      id,
      serviceAddress,
      city,
      province,
      premiseType,
      partnerStatus,
      sayb,
      availableTech,
      currentTech,
      tv,
      hsia,
      hp,
      mob,
      shs,
      dropStatus,
      fmsId,
      gisDropStatus,
      fibreDropCompletionDate,
      addressComment,
      coid,
      fsa,
      firstNation,
      assetSummary,
      servicesAvailable,
      existingBusinessProducts,
      createdBy,
      createdAt,
      modifiedBy,
      modifiedAt,
      addressActivity,
      knocks,
      dropPermission,
    } = address360;

    this.id = id;
    this.serviceAddress = serviceAddress;
    this.city = city;
    this.province = province;
    this.premiseType = premiseType;
    this.partnerStatus = partnerStatus;
    this.sayb = sayb;
    this.dropStatus = dropStatus;
    this.currentTech = currentTech;
    this.availableTech = availableTech;
    this.addressComment = addressComment;
    this.coid = coid;
    this.fsa = fsa;
    this.firstNation = firstNation;
    this.tv = tv;
    this.hsia = hsia;
    this.hp = hp;
    this.mob = mob;
    this.shs = shs;
    this.assetSummary = assetSummary;
    this.servicesAvailable = servicesAvailable;
    this.existingBusinessProducts = existingBusinessProducts;
    this.fmsId = fmsId;
    this.gisDropStatus = gisDropStatus;
    this.fibreDropCompletionDate = fibreDropCompletionDate;
    this.createdBy = createdBy;
    this.createdAt = createdAt;
    this.modifiedBy = modifiedBy;
    this.modifiedAt = modifiedAt;
    this.addressActivity = addressActivity;
    this.knocks = knocks;
    this.dropPermission = dropPermission;
  }
}
